import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  Alert, Badge, DataTable, DefinitionList, Drawer, EmptyState, FilterBar, PageHeader, Segmented, Stack, t, Text,
} from '@kanbios/ui';
import { useEnvironmentFilter } from '../../components/EnvironmentPicker';
import type { maintenance } from '@kanbios/api-client';
import { Shell } from '../../components/Shell';
import { RefusalPage } from '../../components/Refusal';
import { api, unwrap } from '../../lib/api';
import { relativeTime, shortDate } from '../../lib/format';
import { useFillHeight } from '../../lib/useFillHeight';
import { DomainsTabs } from './DomainsTabs';
import { healthColumn, HealthLegend } from '../../components/StateColumn';
import { ToneView, toneViewItems } from '../../components/domains/toneViews';

type Domain = maintenance.components['schemas']['DnsDomain'];
const TONES = { expires_7: 'neg', dnssec_error: 'neg', expires_30: 'warn', expires_60: 'warn', expiry_unknown: 'neutral', ok: 'pos' } as const;
const FACETS = ['situation', 'client', 'registrar'] as const;
const SITUATIONS = Object.keys(TONES) as Domain['situation'][];
const situationSpec = (s: Domain['situation']) => ({ tone: TONES[s], label: t(`domains.situation.${s}`) });

/**
 * Domaines et DNS (SPEC-029), DSM index page: the clients' domains, their
 * expiry read by RDAP; the detail in a Drawer whose address can be shared
 * (`?domain=`). Records monitoring arrives with lot 5 (RG-029-10).
 */
export function DomainsPage() {
  const fill = useFillHeight();
  const [params, setParams] = useSearchParams();
  const [view, setView] = useState<ToneView>('all');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [page, setPage] = useState(1);
  // D73: the global environment filter.
  const env = useEnvironmentFilter();
  const filterParam = Object.fromEntries(Object.entries(filters).filter(([, v]) => v.length).map(([k, v]) => [k, v.join(',')]));
  const list = useQuery({
    queryKey: ['domains', env, view, search, filterParam, page],
    queryFn: async () => unwrap(await api.maintenance.GET('/domains', { params: { query: { env, view, q: search || undefined, page, pageSize: 25, filter: filterParam } } })),
    placeholderData: keepPreviousData,
  });
  const open = params.get('domain');
  const crumbs = [{ label: t('nav.domains'), href: '/domains' }, { label: t('domains.title') }];
  if (list.error) return <Shell crumbs={crumbs}><RefusalPage error={list.error} /></Shell>;

  const labelOf = (facet: string, value: string) =>
    facet === 'situation' ? t(`domains.situation.${value}`) : facet === 'client' ? list.data?.labels[value] ?? value : value;
  return (
    <Shell crumbs={crumbs}>
      <Stack gap={24}>
        <PageHeader title={t('domains.title')} subtitle={t('domains.subtitle')} />
        <DomainsTabs value="names" />
        <Segmented label={t('domains.views')} size="sm" value={view} onChange={v => { setView(v as ToneView); setPage(1); }}
          items={toneViewItems('domains', list.data?.views)} />
        <div ref={fill[0]}>
          <DataTable
            height={fill[1]}
            label={t('domains.table')}
            rows={list.data?.items ?? []}
            rowKey="name"
            manual
            totalCount={list.data?.total ?? 0}
            loading={list.isPending}
            toolbarStart={
              <FilterBar label={t('domains.filters')} search={search} onSearchChange={q => { setSearch(q); setPage(1); }} searchPlaceholder={t('domains.search')}
                value={filters} onChange={v => { setFilters(v as Record<string, string[]>); setPage(1); }}
                filters={FACETS.map(key => ({ key, label: t(`domains.facet.${key}`), type: 'multi' as const,
                  options: Object.entries(list.data?.facets[key] ?? {}).map(([value, n]) => ({ value, label: `${labelOf(key, value)} (${n})` })) }))} />
            }
            toolbarSearch={false}
            toolbarFilters={false}
            density="compact"
            onRowClick={(d: Domain) => setParams({ domain: d.name })}
            activeRowKey={open ?? undefined}
            columns={[
              // Its situation (D57, a dot first), the domain, who it belongs to, then its registry facts.
              healthColumn<Domain>({
                legendLabel: t('domains.legend'), legendTitle: t('domains.legendTitle'),
                legend: <HealthLegend items={SITUATIONS.map(situationSpec)} />,
                spec: d => situationSpec(d.situation),
              }),
              { key: 'name', label: t('domains.columns.name'), mono: true, width: 240, minWidth: 200, pinned: 'left' as const },
              { key: 'client', label: t('domains.columns.attachment'), minWidth: 200, grow: true, render: (d: Domain) => (d.projectTitle ? `${d.clientTitle} › ${d.projectTitle}` : t('domains.wholeClient', { client: d.clientTitle })) },
              { key: 'expiresAt', label: t('domains.columns.expires'), width: 210, render: (d: Domain) => (d.expiresAt && d.daysLeft !== null ? t('domains.expiresIn', { n: d.daysLeft, date: shortDate(d.expiresAt) }) : t('domains.unknown')) },
              { key: 'registrar', label: t('domains.columns.registrar'), width: 160, render: (d: Domain) => d.registrar ?? d.registrarRead ?? '—' },
              { key: 'dnssec', label: t('domains.columns.dnssec'), width: 100, render: (d: Domain) => t(`domains.dnssec.${d.dnssec}`) },
              { key: 'autoRenew', label: t('domains.columns.autoRenew'), width: 120, hidden: true, render: (d: Domain) => (d.autoRenew === null ? t('domains.unknownShort') : d.autoRenew ? t('domains.yes') : t('domains.no')) },
            ]}
            empty={<EmptyState framed={false} icon="language" title={view === 'all' ? t('domains.empty.none') : t('domains.empty.view', { view: t(`domains.view.${view}`) })} />}
            pagination={{ page, pageSize: 25, total: list.data?.total ?? 0, onPageChange: setPage }}
          />
        </div>
      </Stack>
      <DomainDrawer name={open} onClose={() => setParams({})} />
    </Shell>
  );
}

function DomainDrawer({ name, onClose }: { name: string | null; onClose: () => void }) {
  const detail = useQuery({
    queryKey: ['domain', name],
    queryFn: async () => unwrap(await api.maintenance.GET('/domains/{domain}', { params: { path: { domain: name! } } })),
    enabled: Boolean(name),
    retry: false,
  });
  const d = detail.data;
  return (
    <Drawer open={Boolean(name)} onClose={onClose} size="xl" title={name ?? ''}
      subtitle={d ? (d.projectTitle ? `${d.clientTitle} › ${d.projectTitle}` : t('domains.wholeClient', { client: d.clientTitle })) : undefined}>
      {detail.error && <RefusalPage error={detail.error} />}
      {d && (
        <Stack gap={16}>
          <Badge tone={TONES[d.situation]}>{t(`domains.situation.${d.situation}`)}</Badge>
          {d.unreadable && <Alert tone="warn">{t('domains.rdapDown')}</Alert>}
          <DefinitionList items={[
            { label: t('domains.columns.registrar'), value: `${d.registrar ?? d.registrarRead ?? '—'}${d.registrarManaged ? ` · ${t('domains.managed')}` : ''}` },
            { label: t('domains.columns.expires'), value: d.expiresAt && d.daysLeft !== null ? t('domains.expiresIn', { n: d.daysLeft, date: shortDate(d.expiresAt) }) : t('domains.unknown') },
            { label: t('domains.columns.autoRenew'), value: d.autoRenew === null ? t('domains.unknownShort') : d.autoRenew ? t('domains.yes') : t('domains.no') },
            { label: t('domains.columns.dnssec'), value: t(`domains.dnssec.${d.dnssec}`) },
            { label: t('domains.nameservers'), value: d.nameservers.join(', ') || '—', mono: true },
            { label: t('domains.certificate'), value: t('domains.certificateLater') },
            { label: t('domains.checkedAt'), value: d.checkedAt ? relativeTime(d.checkedAt) : t('domains.never') },
          ]} />
          {!d.registrarManaged && <Alert tone="info">{t('domains.manualRenewal', { registrar: d.registrar ?? '—' })}</Alert>}
          <Text variant="body-sm" tone="mute">{t('domains.recordsLater')}</Text>
        </Stack>
      )}
    </Drawer>
  );
}
